import { useMemo, useState } from 'react'

import { WeatherActualCondition } from '@ebalance/proto/bffuser/v1/weather_actual_model_pb'
import { useTranslation } from 'react-i18next'
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { MouseHandlerDataParam } from 'recharts'

import {
  Box,
  Flex,
  LightModeSemanticColors,
  Text,
  fontSizes,
} from '@enechain/ecloud-designsystem'

import {
  AwaitingVoltageHatchPattern,
  useAwaitingVoltageHatchId,
} from '~/components/balance/AwaitingVoltageMark'
import { useDemandTooltipProps } from '~/components/balance/DemandTooltip'
import type {
  DemandTooltipKoma,
  DemandTooltipVisibility,
} from '~/components/balance/DemandTooltip'
import { ChartMarkerLabels } from '~/components/common/ChartMarkerLabels'
import { LeftAlignedYAxisTick } from '~/components/common/LeftAlignedYAxisTick'
import {
  BALANCE_AWAITING_VOLTAGE_HATCH,
  BALANCE_DEMAND_SERIES_COLORS,
  BALANCE_VOLTAGE_BAND_SEPARATOR_COLOR,
  BALANCE_VOLTAGE_CLASS_COLORS,
} from '~/constants/chartPalette'
import { KOMA_COUNT } from '~/constants/koma'
import { typography } from '~/constants/typography'
import { awaitingKomaRanges } from '~/models/awaitingVoltage'
import { formatChartTick } from '~/models/chartFormatting'
import {
  isTargetDateBeforeTodayInJst,
  isTargetDateTodayInJst,
  toProtoDate,
} from '~/models/dateTime'
import { komaNumberToTime } from '~/models/komaToTime'
import { hasVoltageBreakdown, topVoltageClass } from '~/models/voltageBreakdown'
import type { WeatherActualKoma } from '~/models/weather/actualKoma'
import {
  buildWeatherStripDisplayIcons,
  weatherStripGutterWidths,
} from '~/models/weather/koma'
import { isDaytimeHour } from '~/models/weather/time'
import { weatherActualEmojiOf } from '~/models/weather/vpfd'
import type { DetailKomaData } from '~/pages/BalanceDemandDetail/models/generateMockData'
import {
  hoveredKomaIndexOf,
  weatherStripHighlightCells,
} from '~/pages/BalanceDemandDetail/models/weatherStripHighlight'

const SERIES_COLORS = BALANCE_DEMAND_SERIES_COLORS

type Props = {
  data: DetailKomaData[]
  gcKoma: number
  /**
   * GC 縦線 / 現在時刻線 / GC 以降の網掛けを描くか。 既定 true。
   *
   * 取得失敗の空状態 (AC-BDB-033) で false にする。 空状態の器は `gcKoma: 0` を
   * 持つが**これは実値ではない**ので、 そのまま描くと「GC 00:00」 が確定値の顔で
   * 出る (2026-09-09 レビュー指摘。 SummaryBar 側は `gcKoma: null` で塞いだが
   * チャート 2 つが素通しだった)。
   */
  showGcMarkers?: boolean
  currentKoma: number
  /** 表示中の日付。当日以外は現在時刻縦線・GC 縦線・GC 以降網掛け・
   *  ChartMarkerLabels をいずれも描画しない (spec AC-BDB-022a)。 */
  displayDate: Date
  /** 系列可視性 (= ツールチップと同じ実効値。ページ側で親トグルを畳み込み済み)。 */
  seriesVisibility: DemandTooltipVisibility
  /** 天気アイコン帯 (予報。48 スロット)。
   *  出典は weather-icon-unify-48koma 設計書 AC-07/09 だが、 **描画位置は #5087 で改訂済み**
   *  (run 中央配置 → 3 時間ごとの固定ピッチ)。 詳細は `buildWeatherStripDisplayIcons` の JSDoc。 */
  weatherIcons: string[]
  /** ライブ予報の予想気温ラベル (48 スロット、正時のみ整数 ℃・単位なし)。 */
  weatherTemps: string[]
  /**
   * 実績気象 (48 コマ)。過去区間 (現在コマより前) の天気帯はこちらを、
   * 未来区間は予報 (weatherIcons / weatherTemps) を使う。
   *
   * **値は 48 コマ全部を渡す**が、 天気帯に出す気温ラベルは正時のみ (1 時間おき)。
   * 想定行と同じ密度に揃えるための間引きで、 チャート・ツールチップ・コマ表の
   * 粒度には影響しない (2026-09-04、 実績行だけ倍の密度で値が重なっていた)。
   * 天気アイコンは従来どおり 30 分刻みで切り替わる。
   */
  weatherActual: WeatherActualKoma[]
}

// チャート plot 領域の実寸。天気帯のスペーサーと Recharts の YAxis width /
// margin の両方がこれを参照し、アイコン位置と X 軸時刻の整列を保つ。
// margin.left は 0 (負値にすると需要の 5〜6 桁 MWh 目盛りラベルが左端で
// 見切れる)。WeatherChart も同値で揃え、上下チャートのプロット左端を一致させる。
const Y_AXIS_WIDTH = 52
const CHART_MARGIN_LEFT = 0
const CHART_MARGIN_RIGHT = 2
// 右軸の予約幅。需要チャートに右軸目盛りは無いが、下の気象チャート (右軸=降水量 mm)
// と同じ幅を予約して目盛りだけ隠すことで、上下チャートのプロット右端を揃える。
const RIGHT_AXIS_WIDTH = 40
/** 天気帯の左右ガター。軸幅から半コマぶん削り、48 等分セルの中央を X 軸の
 *  コマ位置 (`scale="point"`) に載せる (`weatherStripGutterWidths` の JSDoc。#5087)。 */
const WEATHER_STRIP_GUTTERS = weatherStripGutterWidths(
  Y_AXIS_WIDTH + CHART_MARGIN_LEFT,
  RIGHT_AXIS_WIDTH + CHART_MARGIN_RIGHT,
)

/**
 * 天気帯の 1 行 (design bg-region の dw-row)。左ガターに行ラベル (想定/実績)、
 * その右にプロット幅を 48 等分したセル (アイコン + 気温) を並べる。左右ガター幅は
 * チャートの Y 軸幅 / 右軸幅から半コマぶん削った値で、セル中央が X 軸の時刻ラベル
 * (コマ位置) に一致する。行ラベルは右揃えなのでガターと一緒に半コマぶん左へ動き、
 * 00:00 セルとの間隔は変わらない。
 *
 * ただし削る半コマはプロット幅に比例するので、右ペインが極端に広い環境では
 * ガターがラベル 1 行ぶんを割り込む (52px 軸から 6px の padding を引いた内寸
 * 46px に対し、半コマ 26px = ペイン幅 2,538px ≒ 4K モニタ全画面で 20px を切る。
 * 英語 UI の "Forecast" はもっと早く割り込む)。行ラベル枠の右揃えを
 * `justify-content: safe flex-end` で行うのはこのため — 収まる間は右揃え、
 * 収まらなくなったら start 揃えへ退避するので、ラベルが枠の左へはみ出して
 * 右ペイン (`overflow-y: auto` = x 方向も auto) に切り落とされることがない。
 * 退避後の描画位置は枠の左端 (= 行の左端) で、00:00 セルのアイコン中心は
 * プロット左端 (= 素の軸幅 52px の位置) にあるため重ならない。
 */
const WeatherStripRow = ({
  label,
  labelColor,
  icons,
  temps,
  dimPast,
  currentKoma,
  highlightCells,
}: {
  label: string
  /** 行ラベル色 (design balance.css: 想定=text-mid-emphasis / 実績=text-positive-high)。 */
  labelColor: string
  icons: string[]
  temps: string[]
  /** 過去コマ (現在コマより前) を淡色化するか (想定行のみ true)。 */
  dimPast: boolean
  currentKoma: number
  /** ハイライトするセル (AC-BDB-010c。 `weatherStripHighlightCells`)。 */
  highlightCells: ReadonlySet<number>
}): React.ReactElement => (
  // 行の縦揃えは **flex-end**。 セルは「アイコン枠 16px + 気温」の縦積みなので、
  // `center` だと行の中央 (= アイコンと気温の間) にラベルが来て、 右の気温より
  // **8.5px 上**にずれる (実測。 2026-09-04 デザイナー指摘)。 下端で揃えると
  // ラベルと気温が同じ段に並ぶ。
  //
  // `baseline` は使わない — アイコン枠 (16px の Flex) が最初の行ボックスになるため
  // そちらの上端に揃い、 かえって 16〜20px ずれる (実測)。
  <Flex alignItems="flex-end">
    {/* 行ラベル枠 = 左ガター。`justifyContent="safe flex-end"` は「収まる間は
        右揃え / 収まらなくなったら start 揃えへ退避」(本 component 冒頭 JSDoc)。
        `textAlign="right"` だと溢れは必ず左 = 切り落としになる。 */}
    <Flex
      alignItems="center"
      justifyContent="safe flex-end"
      flexShrink={0}
      width={WEATHER_STRIP_GUTTERS.left}
      paddingRight="6px"
    >
      {/* 行ラベルは canonical role の captionStrong (sm/semibold = 12px/600)。
          以前は 10px/600 を raw で書き「役割 textStyle と一致しない」 としていたが、
          design (`demand/forecast-today.html` の `.weather-row-labels`) は 12px/600 で
          captionStrong と同値だった。 需要想定日次の天気帯 (WeatherBar) も同時に
          captionStrong へ揃えている (デザイナー判断 2026-08-28)。
          native span なので chakra の textStyle は使えず、 raw 版の
          `typography.captionStrong` を消費する。 */}
      <span
        style={{
          ...typography.captionStrong,
          color: labelColor,
          whiteSpace: 'nowrap',
          // `captionStrong` の行高 (1.5) が下に余白を作り、 flex-end で揃えても
          // 気温より 2.3px 上に残る。 行高を字面に詰めて 0.8px まで寄せる。
          lineHeight: 1,
        }}
      >
        {label}
      </span>
    </Flex>
    {icons.map((icon, i) => {
      // アイコンの無いセル (未来の予報なし正時以外など) は光らせる対象が無い (AC-BDB-010c ※)。
      const highlighted = icon !== '' && highlightCells.has(i)
      return (
        <Flex
          key={i}
          alignItems="center"
          flexDirection="column"
          flex={1}
          gap="1px"
          minWidth={0}
          opacity={dimPast && i < currentKoma ? 0.55 : 1}
        >
          {/* アイコン枠は空でも 16px を確保する。潰れると気温のベースラインが
              アイコン有無で上下してガタつくため (高さ固定で全気温を同一段に揃える)。 */}
          <Flex
            alignItems="center"
            justifyContent="center"
            minWidth={0}
            height="16px"
          >
            {/* 強調表現は需要想定日次の天気帯 (WeatherBar) と同じ「控えめな拡大 +
                背景色」。 拡大は transform なのでレイアウトを動かさない。 */}
            <Text
              as="span"
              borderRadius="full"
              backgroundColor={
                highlighted ? 'Surface.accentPrimaryLightLow' : undefined
              }
              style={{
                fontSize: '13px',
                lineHeight: '16px',
                transform: highlighted ? 'scale(1.25)' : undefined,
                transition: 'transform 0.08s, background-color 0.08s',
              }}
            >
              {icon}
            </Text>
          </Flex>
          <span
            style={{
              fontSize: '9px',
              color: LightModeSemanticColors.Text.midEmphasis.value,
              fontWeight: 500,
            }}
          >
            {temps[i] ?? ''}
          </span>
        </Flex>
      )
    })}
    {/* 右ガター。中身を持たない純スペーサーなので role も可視文字列も無い (同上)。 */}
    <Box
      flexShrink={0}
      width={WEATHER_STRIP_GUTTERS.right}
      marginLeft={WEATHER_STRIP_GUTTERS.rightPull}
    />
  </Flex>
)

export const DetailChart = ({
  data,
  gcKoma,
  showGcMarkers = true,
  currentKoma,
  displayDate,
  seriesVisibility,
  weatherIcons,
  weatherTemps,
  weatherActual,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const gcTime = useMemo(() => komaNumberToTime(gcKoma + 1), [gcKoma])
  const awaitingRanges = useMemo(() => awaitingKomaRanges(data), [data])
  const hatchId = useAwaitingVoltageHatchId()
  const currentTime = useMemo(
    () => komaNumberToTime(Math.max(currentKoma, 1)),
    [currentKoma],
  )
  // GC 縦線・現在時刻縦線・GC 以降網掛けは「当日の締め切り進行状態」を示すもの
  // であり、過去日 (全コマ確定済み) / 未来日 (全コマ未確定) には意味を持たない
  // (spec AC-BDB-022a、AC-BDB-022 の GC カウントダウン非表示と同じ意図)。判定は
  // gcKoma の値から逆算せず displayDate を直接 JST 基準で判定する — backend
  // `GcKomaForDate`(koma_number.go) は未来日でも JST 23:00 以降は
  // ComputeLockedSlots(gate_close.go) が翌日コマをロック済みとして返すため
  // gcKoma は 0 のままとは限らない (例: 23:10 なら翌日コマ1 がロック済みで
  // gcKoma=1)。gcKoma の値から「0 なら未来日」と推測する実装は実際に壊れる。
  const isTodayDisplay = isTargetDateTodayInJst(toProtoDate(displayDate))
  /** GC 系マーカーを描くか (= 当日表示 かつ 実データあり)。 */
  const showGc = isTodayDisplay && showGcMarkers
  // 天気帯の実績行に出す「過去」コマ数 (過去=実績アイコン、doji-bg.md 単体エリア詳細)。
  // backend の currentKoma は非当日で 0 なので、過去日は全 48 コマを過去として扱う
  // (ダッシュボード `AreaCodeChart` と同じ境界)。
  const pastKomaCount = isTodayDisplay
    ? currentKoma
    : isTargetDateBeforeTodayInJst(toProtoDate(displayDate))
      ? KOMA_COUNT
      : 0
  // 天気帯のハイライト (AC-BDB-010c): チャート上でホバー中のコマ + 現在コマ (当日のみ)。
  const [hoveredKomaIndex, setHoveredKomaIndex] = useState<number>()
  const currentKomaIndex =
    isTodayDisplay && currentKoma >= 1 ? currentKoma - 1 : undefined
  const handleChartMouseMove = (state: MouseHandlerDataParam): void => {
    setHoveredKomaIndex(hoveredKomaIndexOf(data, state.activeTooltipIndex))
  }
  const handleChartMouseLeave = (): void => {
    setHoveredKomaIndex(undefined)
  }
  // 天気帯は design bg-region と同じく「想定 (予報)」「実績」の 2 行構成。
  // 想定行 = 予報アイコンを 3 時間ごとの正時コマ (6 コマおき = 00:00/03:00/…/21:00)
  // に等間隔で置く (#5087。旧実装は「正時かつ直前の正時から天気が変わったコマ」
  // だけに置いていたが、天気変化のタイミング次第で間隔が不規則になっていた)。
  // 6 の倍数は必ず偶数コマ = 正時なので、正時にだけ出る気温ラベルと同じ列に載る。
  // セル数は実績行と同じ weatherActual (常に 48 コマ、buildKomaWeatherActual) に
  // 揃える — 行ごとにセル数が違うと 2 行同士とチャートの整列が壊れるため、予報が
  // 未取得 (weatherIcons が空) でも空セルで 48 個ぶんを確保する。
  const forecastDisplayIcons = useMemo(() => {
    const strip = buildWeatherStripDisplayIcons(weatherIcons)
    return weatherActual.map((_, i) => strip[i] ?? '')
  }, [weatherIcons, weatherActual])
  // 実績行 = 過去 (現在コマより前) の正時コマに導出済み実績天気を毎時表示する。
  // BQ の天気コードは毎時 :00 に届くので run 間引きせず 1 時間 1 個で並べる
  // (間引くと「終日曇り」が 1 個に潰れて情報量が BQ より大きく減る)。未来コマと
  // 導出不能コマ (夜間で天気コードが晴/曇以外等 = UNSPECIFIED) は空。
  const actualDisplayIcons = useMemo(
    () =>
      weatherActual.map((actualKoma, i) =>
        i < pastKomaCount && i % 2 === 0
          ? weatherActualEmojiOf(
              actualKoma?.condition ?? WeatherActualCondition.UNSPECIFIED,
              // 正時コマ (i even) の時 = i/2。予報行 (vpfdEmojiOf) と同じ昼夜窓で
              // 晴を ☀️/🌙 に切り替える (実績行と予報行の昼夜表現を一致させる)。
              isDaytimeHour(i / 2),
            )
          : '',
      ),
    [weatherActual, pastKomaCount],
  )
  // ハイライトするセル = そのコマのアイコンが置かれるセル (AC-BDB-010c)。
  // 予報行: 予報は正時の点以外のコマに割り当てない (AC-WD-003a) ので、そのコマのセルに
  // アイコンがある時だけ (アイコンの無いコマは対象無し。AC-BDB-010c ※)。
  // 実績行: 実績は正時の観測値を 30 分のコマにも据え置くので、同じ時の正時セル
  // (design `js/charts.js` の `Math.floor(index / 2)`)。
  const forecastHighlightCells = useMemo(
    () =>
      weatherStripHighlightCells(hoveredKomaIndex, currentKomaIndex, (koma) =>
        forecastDisplayIcons[koma] ? koma : undefined,
      ),
    [hoveredKomaIndex, currentKomaIndex, forecastDisplayIcons],
  )
  const actualHighlightCells = useMemo(
    () =>
      weatherStripHighlightCells(
        hoveredKomaIndex,
        currentKomaIndex,
        (koma) => koma - (koma % 2),
      ),
    [hoveredKomaIndex, currentKomaIndex],
  )
  /**
   * 天気帯の気温ラベル。 **想定行・実績行とも正時のみ** (= 1 時間おき、最大 24 個)。
   *
   * 実績行だけ 30 分刻み (全 48 コマ) で出しており、 想定行の倍の密度になっていた。
   * 同じ幅に倍の数を詰めるので値どうしがくっつき、 `18℃19℃19℃20℃…` と読めない
   * 帯になっていた (2026-09-04 デザイナー指摘)。
   *
   * design も両行とも正時である: `js/bg-region-data.js` は `weatherIcons.forEach`
   * (= 1 時間おきの 24 要素) で回し、 各セルの気温を `komaData[i * 2]` から取る。
   * 想定・実績で同じ添字を使うので、 密度が行で変わることはない。
   *
   * 値そのものは 30 分刻みで保持したまま (`weatherActual` は 48 コマ)。 間引くのは
   * **帯のラベルだけ**で、 チャート・ツールチップ・コマ表の粒度には影響しない。
   */
  const forecastTempLabels = useMemo(
    () =>
      weatherActual.map((_, i) =>
        i % 2 === 0 && weatherTemps[i] !== undefined && weatherTemps[i] !== ''
          ? `${weatherTemps[i]}℃`
          : '',
      ),
    [weatherActual, weatherTemps],
  )
  const actualTempLabels = useMemo(
    () =>
      weatherActual.map((actualKoma, i) =>
        i % 2 === 0 && i < pastKomaCount && actualKoma?.temperature != null
          ? `${actualKoma.temperature}℃`
          : '',
      ),
    [weatherActual, pastKomaCount],
  )

  // 内訳が総量と整合しない時に積み上げへ切り替えると実績を過小表示するため、
  // 整合する時のみ切り替える (hasVoltageBreakdown の godoc 参照)。
  const dataHasBreakdown = hasVoltageBreakdown(data)
  const voltage = {
    extraHigh: seriesVisibility.voltageExtraHigh && dataHasBreakdown,
    high: seriesVisibility.voltageHigh && dataHasBreakdown,
    low: seriesVisibility.voltageLow && dataHasBreakdown,
  }
  // 1 階級でも描いていれば「内訳表示中」= 実績値は塗りを帯に譲り輪郭線になる
  // (AC-BDB-026)。全階級 OFF なら通常の実績エリアへ戻す。
  const showVoltageBreakdown = voltage.extraHigh || voltage.high || voltage.low
  // 帯の境界線 (design js/charts.js voltArea)。最上段は自色の輪郭、それ以外は
  // 地色で 1px 抜く。最上段は描かれる階級によって変わるので固定にしない。
  const voltageTop = topVoltageClass(data, voltage)
  // ツールチップにも実効値を渡す。トグルの生値を渡すと、チャートが積み上げを
  // 抑止しているコマでもツールチップだけが不完全な内訳 (総量と対応しない値) を
  // 出してしまう。
  const tooltipVisibility = useMemo(
    () => ({
      ...seriesVisibility,
      voltageExtraHigh: voltage.extraHigh,
      voltageHigh: voltage.high,
      voltageLow: voltage.low,
    }),
    [seriesVisibility, voltage.extraHigh, voltage.high, voltage.low],
  )
  // 予想気温は需要の行に載らない (気象予報 gRPC 由来) ので、下の気象チャートと
  // 同じ系列をコマ index で渡す (AC-BDB-010b / AC-WD-003a: 正時の点のコマだけ値を持つ)。
  const temperatureForecasts = useMemo(
    () => weatherActual.map((k) => k.temperatureForecast),
    [weatherActual],
  )
  const { chartRef, ...tooltipProps } = useDemandTooltipProps({
    visibility: tooltipVisibility,
    weatherIcons,
    temperatureForecasts,
    gcKoma,
    currentKoma,
  })

  const axisTickStyle = {
    fontSize: 11,
    fill: LightModeSemanticColors.Text.midEmphasis.value,
  }

  return (
    <Flex
      flexDirection="column"
      flex={1}
      // ツールチップは body 直下へ portal するのでカードの overflow には掛からない。
      // visible の明示は、チャート本体の収まりを minHeight 側に委ねている印として残す。
      overflow="visible"
      // カード高の床 (design balance.css `.region-right > .chart-card`)。
      // minHeight=0 で残り高さに完全追従させると、上のフィルタカードが 1 行
      // 増えただけでプロットが 0px まで潰れ、チャートが無言で消える (#4515 の
      // 「準備中」ラベル追加でこれを踏んだ)。足りない高さは親ペインの
      // 縦スクロールへ逃がす (design も region-right を overflow-y:auto にしている)。
      minHeight="260px"
      borderWidth="1px"
      borderColor="Border.lowEmphasis"
      borderRadius="md"
      backgroundColor="Surface.primary"
    >
      {/* Chart title (design bg-region: chart-card-title「需要・太陽光（計画・実績）」)。
          [design差異] 太陽光系列はデータソース未確定 (#2002) のため画面から削除
          (#5282, doji-bg.md Discussion)。design はまだ太陽光入りの表題のまま
          だが、実装は太陽光を落とした表題 (page:balanceDemandDetail.chart.title)
          を使う。

          見た目は design `css/balance.css:367` の `.chart-card-title`
          (padding 8px 12px / md・700 / 地 surface-secondary / 下罫線 border-low)。
          実装は素の 16px/600 テキストで、地も罫線も無かった (#7064)。

          カードは overflow:visible (ツールチップを body へ portal するため) なので、
          ヘッダ帯に自前で上側の角丸を持たせないとカードの角からはみ出す。 */}
      <Flex
        flexShrink={0}
        padding="8px 12px"
        borderBottomWidth="1px"
        borderBottomColor="Border.lowEmphasis"
        borderTopRadius="md"
        backgroundColor="Surface.secondary"
      >
        <Text textStyle="chartCardTitle" color="Text.highEmphasis">
          {t('page:balanceDemandDetail.chart.title')}
        </Text>
      </Flex>

      {/* Weather strip: design bg-region と同じく「想定 (予報)」「実績」の 2 行。
          各行はプロット幅を 48 等分し X 軸の時刻ラベルと整列させる。想定は 3 時間
          ごとの正時コマ (6 コマおき = 8 個、#5087)、実績は過去コマのみ毎時 (最大
          24 個) なので、行によってアイコンの密度が変わる。 */}
      {seriesVisibility.weatherStrip && (
        <Box
          padding="3px 0"
          borderBottomWidth="1px"
          borderBottomColor="Border.lowEmphasis"
        >
          <WeatherStripRow
            currentKoma={currentKoma}
            dimPast
            highlightCells={forecastHighlightCells}
            icons={forecastDisplayIcons}
            label={t('page:balanceDemandDetail.chart.forecastRowLabel')}
            labelColor={LightModeSemanticColors.Text.midEmphasis.value}
            temps={forecastTempLabels}
          />
          <WeatherStripRow
            currentKoma={currentKoma}
            dimPast={false}
            highlightCells={actualHighlightCells}
            icons={actualDisplayIcons}
            label={t('page:balanceDemandDetail.chart.actualRowLabel')}
            labelColor={LightModeSemanticColors.Text.positiveHigh.value}
            temps={actualTempLabels}
          />
        </Box>
      )}

      {/* Axis labels: 左=MWh 単位、右=現在/GC マーカー (通常フロー行。プロット右端
          = 右軸幅 + 右マージン に右揃えし、WeatherChart 側と同一計算で位置を揃える)。 */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1px 0 0 0',
          fontSize: fontSizes.xs.value,
          fontWeight: 600,
          color: LightModeSemanticColors.Text.midEmphasis.value,
          lineHeight: 1,
          flexShrink: 0,
        }}
      >
        <span>{t('page:balanceDemandDetail.chart.kwh')}</span>
        {showGc && (
          <ChartMarkerLabels
            markers={[
              {
                time: currentTime,
                text: t('page:balanceDemandDetail.chart.now'),
                color: LightModeSemanticColors.Surface.accentPrimary.value,
              },
              {
                time: gcTime,
                text: `${t('page:balanceDemandDetail.chart.gc')} ${gcTime}`,
                /* GC の赤は **`Surface.negativeHigh`** (#d80004)。 `Text.negativeHigh` (#870103) は
                   暗いマルーンで、 design の GC 色 (`js/charts.js:876` `cssVar('surface-negative-high')`)
                   と別物 (2026-09-03 デザイナー指摘 #7064)。 線・ラベル・バッジで同じ 1 色を使う。 */
                color: LightModeSemanticColors.Surface.negativeHigh.value,
              },
            ]}
            rightOffset={RIGHT_AXIS_WIDTH + CHART_MARGIN_RIGHT}
          />
        )}
      </div>

      {/* Chart: minHeight=0 で残り高さに完全追従させる (固定 floor を置くと
          タイトル/天気帯ぶんで縦が溢れ、X 軸が下端で見切れるため)。 */}
      {/*
       * css で recharts の目盛テキストを 11px に固定する: Chakra preflight の
       * `"*": { font: "inherit" }` が全要素に効き、Recharts の `tick={{ fontSize }}`
       * (= SVG の presentation 属性 `font-size`) が body 継承値 (14px) に負けて効かない
       * (Supply48DataChart.tsx と同じ罠。値の出所は `axisTickStyle` / 左 Y 軸の
       * `LeftAlignedYAxisTick` のままにし、効かせ方だけ css 経由に変える)。
       * この画面は全軸 11px で揃っているため selector は 1 本でよい。
       */}
      <Box
        ref={chartRef}
        flex={1}
        minHeight={0}
        css={{ '& .recharts-text': { fontSize: '11px' } }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            margin={{
              top: 18,
              right: CHART_MARGIN_RIGHT,
              left: CHART_MARGIN_LEFT,
              bottom: 2,
            }}
            // satisfies: 行型が共有ツールチップの契約を満たすことを compile time
            // で固定する (RegionChart 側と同じ理由)。
            data={data satisfies DemandTooltipKoma[]}
            onMouseLeave={handleChartMouseLeave}
            onMouseMove={handleChartMouseMove}
          >
            <CartesianGrid
              stroke={LightModeSemanticColors.Border.lowEmphasis.value}
            />
            <XAxis
              dataKey="time"
              interval={0}
              // point スケールで明示 (下の気象チャートと座標系を一致させ、GC/現在
              // 縦線の x を揃える。band になると半コマぶんズレる)。
              scale="point"
              tick={axisTickStyle}
              tickFormatter={formatChartTick}
            />
            <YAxis
              // 最大値の真上に張り付かないよう上端に約 20% の余白を持たせる
              // (0 始まりの棒/面なので下端は 0 固定)。
              width={Y_AXIS_WIDTH}
              domain={[0, (dataMax: number) => Math.ceil(dataMax * 1.2)]}
              tick={<LeftAlignedYAxisTick />}
              yAxisId="left"
            />
            {/* 右軸は使わないが、下の気象チャートの右軸 (降水量) と同幅を予約して
                プロット右端を揃える (目盛り・軸線は非表示)。 */}
            <YAxis
              width={RIGHT_AXIS_WIDTH}
              axisLine={false}
              domain={[0, 10]}
              orientation="right"
              tick={false}
              tickLine={false}
              yAxisId="right"
            />
            <Tooltip {...tooltipProps} />

            {/* Areas (基本表示トグルで表示切替) */}
            {/* 電圧階級別 (AC-BDB-026): 階級ごとに個別 ON/OFF でき、OFF の階級は
                stackId を共有する子ごと外れるので積み上げが詰まる。凡例色は一覧カード
                (RegionChart) と同一に揃える。実績の輪郭線より先に置く — 後に置くと
                帯の塗りに覆われる。 */}
            {voltage.low && (
              <Area
                fill={BALANCE_VOLTAGE_CLASS_COLORS.voltageLow}
                stroke={
                  voltageTop === 'low'
                    ? BALANCE_VOLTAGE_CLASS_COLORS.voltageLow
                    : BALANCE_VOLTAGE_BAND_SEPARATOR_COLOR.detailChart
                }
                dataKey="demandLow"
                dot={false}
                fillOpacity={0.85}
                isAnimationActive={false}
                stackId="volt"
                strokeWidth={voltageTop === 'low' ? 1.5 : 1}
                type="stepAfter"
                yAxisId="left"
              />
            )}
            {voltage.high && (
              <Area
                fill={BALANCE_VOLTAGE_CLASS_COLORS.voltageHigh}
                stroke={
                  voltageTop === 'high'
                    ? BALANCE_VOLTAGE_CLASS_COLORS.voltageHigh
                    : BALANCE_VOLTAGE_BAND_SEPARATOR_COLOR.detailChart
                }
                dataKey="demandHigh"
                dot={false}
                fillOpacity={0.85}
                isAnimationActive={false}
                stackId="volt"
                strokeWidth={voltageTop === 'high' ? 1.5 : 1}
                type="stepAfter"
                yAxisId="left"
              />
            )}
            {voltage.extraHigh && (
              <Area
                fill={BALANCE_VOLTAGE_CLASS_COLORS.voltageExtraHigh}
                stroke={
                  voltageTop === 'extraHigh'
                    ? BALANCE_VOLTAGE_CLASS_COLORS.voltageExtraHigh
                    : BALANCE_VOLTAGE_BAND_SEPARATOR_COLOR.detailChart
                }
                dataKey="demandExtraHigh"
                dot={false}
                fillOpacity={0.85}
                isAnimationActive={false}
                stackId="volt"
                strokeWidth={voltageTop === 'extraHigh' ? 1.5 : 1}
                type="stepAfter"
                yAxisId="left"
              />
            )}

            {/* 実績値: 色・塗り・輪郭線は design charts.js の実績値 Area 準拠
                (chart-1 / fillOpacity 0.4 / strokeWidth 1.5)。輪郭線を落とすと
                上に重なる実績値(補完) と塗りが混ざって境界が読めなくなる。
                内訳表示中は塗りを帯に譲り、合計の輪郭線として残す (AC-BDB-026) —
                階級を個別に消すと積み上げの天井が総量でなくなるため、総量はこの線で
                読む。トグル OFF の時は輪郭線も出さない (AC-BDB-031 が優先)。 */}
            {seriesVisibility.demandActual && (
              <Area
                fill={SERIES_COLORS.demandActual}
                stroke={SERIES_COLORS.demandActual}
                dataKey="demandActual"
                dot={false}
                fillOpacity={showVoltageBreakdown ? 0 : 0.4}
                isAnimationActive={false}
                strokeWidth={showVoltageBreakdown ? 2 : 1.5}
                type="stepAfter"
                yAxisId="left"
              />
            )}

            {/* 実績値(補完): 補完済みコマの実績値そのもの (= 補完後・送電端値)。
                色・塗り・輪郭線は design charts.js の実績値(補完) Area 準拠
                (chart-6 / fillOpacity 0.3 / strokeWidth 1.5)。輪郭線は design
                コメントどおり「薄塗りでも形が消えない」ための必須要素で、寄与が
                1% 未満のコマでも線として読める。
                [design 逸脱] design は本系列を実績値の *下* に描くが、それは
                mock データが corrected = actual + jitter (実績より大きい) 前提の
                順序である。実データの補完寄与は実績の内数なので同じ順にすると
                半透明の実績塗りに必ず覆われて不可視になる。順序のみ反転する。
                内訳表示中の扱いは実績値と同じ (塗りを落として輪郭線で残す)。 */}
            {seriesVisibility.actualCorrected && (
              <Area
                fill={SERIES_COLORS.demandActualCorrected}
                stroke={SERIES_COLORS.demandActualCorrected}
                dataKey="demandActualCorrected"
                dot={false}
                fillOpacity={showVoltageBreakdown ? 0 : 0.3}
                isAnimationActive={false}
                strokeWidth={showVoltageBreakdown ? 2 : 1.5}
                type="stepAfter"
                yAxisId="left"
              />
            )}

            {/* Lines */}
            {seriesVisibility.demandPlan && (
              <Line
                stroke={SERIES_COLORS.demandPlan}
                dataKey="demandPlan"
                dot={false}
                isAnimationActive={false}
                strokeWidth={2.5}
                type="stepAfter"
                yAxisId="left"
              />
            )}
            <Line
              stroke={LightModeSemanticColors.Text.midEmphasis.value}
              dataKey="trendPlan"
              dot={false}
              isAnimationActive={false}
              strokeDasharray="8 4"
              strokeWidth={2.5}
              type="stepAfter"
              yAxisId="left"
            />
            {seriesVisibility.procurementNet && (
              <Line
                stroke={SERIES_COLORS.procurementNet}
                dataKey="procurementNet"
                dot={false}
                isAnimationActive={false}
                strokeDasharray="6 3"
                strokeWidth={1.5}
                type="stepAfter"
                yAxisId="left"
              />
            )}

            {/* 低圧未着のコマ。コマの属性なので系列トグルにも BG / 小売の
                選択にも従わず、表示日の当日判定にも従わない。 */}
            {awaitingRanges.length > 0 && (
              <defs>
                <AwaitingVoltageHatchPattern patternId={hatchId} />
              </defs>
            )}
            {awaitingRanges.map((range) => (
              <ReferenceArea
                className={BALANCE_AWAITING_VOLTAGE_HATCH.className}
                key={range.x1}
                fill={`url(#${hatchId})`}
                stroke="none"
                fillOpacity={1}
                x1={range.x1}
                x2={range.x2}
                // 需要の系列が左軸に 1 つも無いと左軸が domain を持たず網掛けも消える
                // (GC 以降の網掛けと同じ)。domain 固定の非表示軸は、載る系列が無いと
                // Recharts が scale を作らないので代わりにならない (実測)。
                yAxisId="left"
              />
            ))}

            {/* Reference lines: ラベルは線に付けず ChartMarkerLabels で上端右に
                まとめて描く (現在/GC が近接すると個別ラベルが重なるため)。
                非当日 (過去日・未来日) は描画しない (spec AC-BDB-022a)。 */}
            {showGc && (
              <>
                <ReferenceLine
                  stroke={LightModeSemanticColors.Surface.accentPrimary.value}
                  strokeWidth={1.5}
                  x={currentTime}
                  yAxisId="left"
                />
                <ReferenceArea
                  fill={LightModeSemanticColors.Text.disabled.value}
                  stroke="none"
                  fillOpacity={0.08}
                  x1={gcTime}
                  x2="23:30"
                  yAxisId="left"
                />
                <ReferenceLine
                  stroke={LightModeSemanticColors.Surface.negativeHigh.value}
                  strokeDasharray="4 4"
                  strokeWidth={2}
                  x={gcTime}
                  yAxisId="left"
                />
              </>
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </Flex>
  )
}
